import type { PlanProjectionGroup, PullRequestState } from '@developerz/domain';
import { createMemo, createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { copyText } from '../lib/clipboard';
import { orderedGroups } from '../lib/plan-dag';
import { UNSET } from '../lib/unset';
import { DataTable, type DataTableColumn } from './DataTable';
import { StatusPill, type StatusTone } from './StatusPill';
import { Button } from './ui/Button';

// The Custom plans breakdown's group table (#4341) — one row per slice,
// through `<DataTable>` (`data-table-usage.test.ts` refuses a hand-rolled
// table element anywhere else). Presenter only: <PlanBreakdown/> owns the fetch.
//
// REPO COLUMN IS CONDITIONAL, not always-on: it renders only when the plan's
// groups span more than one repo (arc 101 slice 05 — a plan may, but most
// don't), so a single-repo epic's table doesn't waste a column repeating the
// same name on every row.
//
// DISPOSITION drives the status pill, not `PlanProjectionGroup.status` — the
// DAG rail (`PlanDagRail.tsx`) colours by disposition too, and a reader
// expanding one plan should see ONE vocabulary (landed / in_flight /
// stalled) across both surfaces, not the rail's three-value disposition next
// to the table's five-value `status`.
//
// PR STATE reuses `fleet.epic.pr_open/closed/merged` (`PlanSliceRow.tsx`
// already renders a plan group's PR through the same three keys) rather than
// a new `fleet.plans.custom.*` triplet — same value, same words, no reason
// for two catalogs to disagree on what "Merged" means for a PR.
// `mergeDecision` and `stallReason` are curated under
// `fleet.plans.custom.merge_decision.*` / `fleet.plans.custom.stall.*`,
// enumerating `MergeDecision` (`packages/domain/src/entities/issue.ts`) and
// `PlanGroupStallReason` (`packages/domain/src/value-objects/plan-projection.ts`)
// respectively — a raw domain word (`auto`, `task_failed`, …) is not
// something an operator reading this table should have to decode.

const DISPOSITION_TONE: Record<PlanProjectionGroup['disposition'], StatusTone> = {
  landed: 'success',
  // Released like `landed`, but nothing shipped — a success in tone would claim it did.
  no_work: 'warning',
  in_flight: 'neutral',
  stalled: 'danger',
};

function dispositionLabel(disposition: PlanProjectionGroup['disposition']): string {
  return t(`fleet.plans.custom.disposition.${disposition}`);
}

const PR_STATE_TONE: Record<PullRequestState, StatusTone> = {
  open: 'live',
  closed: 'neutral',
  merged: 'success',
};

const PR_STATE_LABEL: Record<PullRequestState, string> = {
  open: 'fleet.epic.pr_open',
  closed: 'fleet.epic.pr_closed',
  merged: 'fleet.epic.pr_merged',
};

export type PlanGroupTableProps = {
  readonly groups: readonly PlanProjectionGroup[];
};

export function PlanGroupTable(props: PlanGroupTableProps): JSX.Element {
  const rows = createMemo(() => orderedGroups(props.groups));
  const multiRepo = createMemo(() => new Set(props.groups.map((g) => g.repoFullName)).size > 1);

  // ONE signal for the whole table, not one per row: only the row that was
  // just copied ever reads a truthy match, so every other row's cell stays
  // uninvolved in this write.
  const [copiedTaskId, setCopiedTaskId] = createSignal<string | null>(null);
  const copyTaskId = (id: string): void => {
    void copyText(id).then((ok) => setCopiedTaskId(ok ? id : null));
  };

  const columns = createMemo<readonly DataTableColumn<PlanProjectionGroup>[]>(() => {
    const cols: DataTableColumn<PlanProjectionGroup>[] = [
      {
        key: 'position',
        header: t('fleet.plans.custom.col_position'),
        mono: true,
        align: 'end',
        cell: (row) => String(row.position + 1),
      },
      {
        key: 'title',
        rowHeader: true,
        header: t('fleet.plans.custom.col_title'),
        cell: (row) => row.title,
      },
    ];
    if (multiRepo()) {
      cols.push({
        key: 'repo',
        header: t('fleet.plans.custom.col_repo'),
        mono: true,
        cell: (row) => row.repoFullName,
      });
    }
    cols.push(
      {
        key: 'status',
        header: t('fleet.plans.custom.col_status'),
        cell: (row) => (
          <StatusPill tone={DISPOSITION_TONE[row.disposition]}>
            {dispositionLabel(row.disposition)}
          </StatusPill>
        ),
      },
      {
        key: 'task',
        header: t('fleet.plans.custom.col_task'),
        mono: true,
        cell: (row) => (
          <Show when={row.task} fallback={UNSET}>
            {(task) => (
              <span class="plan-group-table__task">
                <code>{task().id}</code>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={t('copy_snippet.aria', { label: t('fleet.plans.custom.col_task') })}
                  onClick={() => copyTaskId(task().id)}
                >
                  {copiedTaskId() === task().id ? t('copy_snippet.copied') : t('copy_snippet.copy')}
                </Button>
              </span>
            )}
          </Show>
        ),
      },
      {
        key: 'pr',
        header: t('fleet.plans.custom.col_pr'),
        cell: (row) => (
          <Show when={row.pullRequest} fallback={UNSET}>
            {(pr) => (
              <span class="plan-group-table__pr">
                <a
                  href={`https://github.com/${row.repoFullName}/pull/${pr().number}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t('fleet.epic.pr', { number: pr().number })}
                </a>
                <StatusPill tone={PR_STATE_TONE[pr().state]}>
                  {t(PR_STATE_LABEL[pr().state])}
                </StatusPill>
                <span class="plan-group-table__merge-decision">
                  {pr().mergeDecision === null
                    ? UNSET
                    : t(`fleet.plans.custom.merge_decision.${pr().mergeDecision}`)}
                </span>
              </span>
            )}
          </Show>
        ),
      },
      {
        key: 'stall',
        header: t('fleet.plans.custom.col_stall'),
        // No longer `mono: true` — that suited a raw enum token
        // (`task_failed`), not the curated sentence it now renders.
        cell: (row) =>
          row.stallReason === null ? UNSET : t(`fleet.plans.custom.stall.${row.stallReason}`),
      },
    );
    return cols;
  });

  return (
    <DataTable
      class="plan-group-table"
      label={t('fleet.plans.custom.breakdown_heading')}
      columns={columns()}
      rows={rows()}
      rowKey={(row) => row.groupId}
    />
  );
}
